import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, Users, Filter, Clock, ChevronRight, Loader2, Target } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { audienceStorage, SavedAudience } from '../services/audienceApi';
import { auditService } from '../services/auditService';
import { toast } from 'sonner';
export function AudienceBuilder() {
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const navigate = useNavigate();
  const [audiences, setAudiences] = useState<SavedAudience[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  useEffect(() => {
    // Show localStorage data immediately for fast render
    setAudiences(audienceStorage.list(currentWorkspace.name));
    // Then sync from Creator (source of truth) to catch any
    // audiences that might be missing from localStorage
    const syncFromBackend = async () => {
      setIsSyncing(true);
      try {
        const synced = await audienceStorage.syncFromCreator(currentWorkspace.name);
        setAudiences(synced);
      } catch (err) {
        console.warn('Failed to sync audiences from Creator:', err);
        // localStorage data is already shown, so this is non-fatal
      } finally {
        setIsSyncing(false);
      }
    };
    syncFromBackend();
  }, [currentWorkspace.name]);
  const handleDelete = async (id: string) => {
    setDeleteConfirm(null);
    const deletedAudience = audiences.find(a => a.id === id);
    try {
      const result = await audienceStorage.delete(id);
      setAudiences(prev => prev.filter(a => a.id !== id));
      if (result.synced) {
        toast.success('Audience deleted');
      } else {
        toast.warning('Audience deleted locally', {
          description: `Creator sync failed: ${result.error}`
        });
      }
      // Fire-and-forget: Audit log
      auditService.log({
        actionType: 'deleted',
        entityType: 'audience',
        entityId: id,
        entityName: deletedAudience?.name || id,
        userName: currentUser.User_Name,
        userEmail: currentUser.Email,
        userRole: currentUser.Role,
        team: currentWorkspace.name,
        region: currentSubRegion || ''
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to delete audience: ${msg}`);
    }
  };
  const filtered = audiences.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q);
  });
  const getRuleSummary = (audience: SavedAudience): string => {
    const count = audience.rules.length;
    if (count === 0) return 'No rules defined';
    const payloadRules = audience.rules.filter(r => r.fieldSource === 'payload').length;
    const standardRules = count - payloadRules;
    const parts: string[] = [];
    if (standardRules > 0) parts.push(`${standardRules} CRM filter${standardRules !== 1 ? 's' : ''}`);
    if (payloadRules > 0) parts.push(`${payloadRules} form data filter${payloadRules !== 1 ? 's' : ''}`);
    return parts.join(' + ') + ` (${audience.logic})`;
  };
  return <div className="space-y-6" data-id="element-2350" data-mp="1krk13n4kysa6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2351" data-mp="1kpz47qusmx95">
        <div className="min-w-0" data-id="element-2352" data-mp="1ft8ox5qtc5os">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2353" data-mp="1fuabo7h6ygmc">
            Audience Builder
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2354" data-mp="3a6i155aqu5aq">
            Create dynamic audiences by filtering contacts using CRM data and
            form submission fields.
            {isSyncing && <span className="inline-flex items-center gap-1 ml-2 text-blue-500" data-id="element-2355" data-mp="3d83c4o850zga">
                <Loader2 className="h-3 w-3 animate-spin" data-id="element-2356" data-mp="3d60at81jbjw9" />
                Syncing...
              </span>}
          </p>
        </div>
        <button onClick={() => navigate('/audiences/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-2357" data-mp="3486ie86w5aed">
          <Plus className="h-4 w-4" data-id="element-2358" data-mp="2ufilzejtp8g6" />
          Create Audience
        </button>
      </div>

      {/* Search */}
      {audiences.length > 0 && <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-2359" data-mp="2kjxnvrhvksbp">
          <div className="relative flex-1 min-w-0" data-id="element-2360" data-mp="2phz5s93kelll">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2361" data-mp="2pgfsmrd7gqo1" />
            <input type="text" placeholder="Search audiences..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-2362" data-mp="2bku6qt29qmzu" />
          </div>
          <span className="text-sm text-gray-500 flex-shrink-0" data-id="element-2363" data-mp="25wz0q8q1wk2s">
            {filtered.length} audience{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>}

      {/* Audience List */}
      {filtered.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-2364" data-mp="2ft22vekwkipq">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-2365" data-mp="2avabclo66ym2">
            <Target className="h-8 w-8 text-gray-400" data-id="element-2366" data-mp="2pjwpik229kbp" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-2367" data-mp="2kmo4mbpj326r">
            {searchQuery ? 'No matching audiences' : 'No audiences yet'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-2368" data-mp="2yj1lpjq5h1kp">
            {searchQuery ? 'Try adjusting your search.' : 'Create your first audience to start filtering contacts based on CRM data and form submissions.'}
          </p>
          {!searchQuery && <button onClick={() => navigate('/audiences/new')} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-2369" data-mp="1w5lv6ylgdpcp">
              <Plus className="h-4 w-4" data-id="element-2370" data-mp="2a066etjad8jp" />
              Create First Audience
            </button>}
        </div> : <div className="space-y-3" data-id="element-2371" data-mp="2ewwkxcffzw30">
          {filtered.map(audience => <div key={audience.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2372" data-mp="2jylz30xyopvb">
              <div className="flex items-center gap-4 p-5" data-id="element-2373" data-mp="2nwtensgovl4x">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-2374" data-mp="pkuo9y3wbzya">
                  <Target className="h-5 w-5 text-blue-600" data-id="element-2375" data-mp="tleml507h9m0" />
                </div>

                <div className="flex-1 min-w-0" data-id="element-2376" data-mp="m2dpb1g00c73">
                  <div className="flex items-center gap-2" data-id="element-2377" data-mp="hb5i8c2552x3">
                    <Link to={`/audiences/${audience.id}`} className="text-sm font-semibold text-gray-900 hover:text-blue-600 truncate" data-id="element-2378" data-mp="h9kmij43bdxn">
                      {audience.name}
                    </Link>
                    {audience.lastCount != null && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-2379" data-mp="14y3uexj89op7">
                        <Users className="h-3 w-3" data-id="element-2380" data-mp="vyqk62na4w3f" />
                        {audience.lastCount}
                      </span>}
                  </div>
                  {audience.description && <p className="text-sm text-gray-500 truncate mt-0.5" data-id="element-2381" data-mp="3gqsg6qlx2o84">
                      {audience.description}
                    </p>}
                  <div className="flex items-center gap-3 mt-1.5" data-id="element-2382" data-mp="1fn9poif6o8c7">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-id="element-2383" data-mp="1fq5nm6r16x0n">
                      <Filter className="h-3 w-3" data-id="element-2384" data-mp="1jna4432a8jj9" />
                      {getRuleSummary(audience)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-id="element-2385" data-mp="1aog9gi8ii38c">
                      <Clock className="h-3 w-3" data-id="element-2386" data-mp="1yeajks1g5dow" />
                      {new Date(audience.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-2387" data-mp="1oohbw24fdipx">
                  <button onClick={e => {
              e.stopPropagation();
              setDeleteConfirm(audience.id);
            }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" title="Delete audience" data-id="element-2388" data-mp="1oo7l4cbk37j6">
                    <Trash2 className="h-4 w-4" data-id="element-2389" data-mp="aocsn8u1i9zp" />
                  </button>
                  <Link to={`/audiences/${audience.id}`} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-2390" data-mp="amjl7rblrlqn">
                    <ChevronRight className="h-4 w-4" data-id="element-2391" data-mp="jj0vksc57flc" />
                  </Link>
                </div>
              </div>
            </div>)}
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2392" data-mp="3iwutwbjq3gxv">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-2393" data-mp="3iyo4g1xgilb5">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-2394" data-mp="s00ife58472" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-2395" data-mp="1c5c891d0x6yi">
              <div className="flex items-center gap-4" data-id="element-2396" data-mp="1770y0pitgufr">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-2397" data-mp="176itl5m039wr">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-2398" data-mp="1f65erq2s5plc" />
                </div>
                <div data-id="element-2399" data-mp="1a5q63btg169p">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2400" data-mp="1a7aq47i4t6tb">
                    Delete Audience
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-2401" data-mp="11d62ry27idij">
                    Are you sure? This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-2402" data-mp="regkw5ab8t2a">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2403" data-mp="rcvqqgrwrc4y">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-id="element-2404" data-mp="2gs3jhuai3aew">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}